import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { ChartLine, Warning, TrendUp, Package, ArrowRight } from "@phosphor-icons/react";

/* ============================================================================
   FinanceReportsTab — combined Finance KPIs + AR aging + 6-month revenue trend
   + Unit Economics (per-case COGS).
   Lives inside /admin/finance under the Reports tab.
   ========================================================================== */

const formatINR = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);


export function FinanceReportsTab() {
    const [aging, setAging] = useState(null);
    const [trend, setTrend] = useState([]);
    const [cogs, setCogs] = useState(null);
    const [cogsIncludeAll, setCogsIncludeAll] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            api.get("/admin/finance/ar-aging"),
            api.get("/admin/finance/revenue-trend?months=6"),
        ])
            .then(([a, t]) => { setAging(a.data); setTrend(t.data || []); })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    /* COGS reloads whenever the include-all toggle flips so the user gets
       instant feedback. Cached separately from aging+trend to avoid double-
       loading those large queries. */
    useEffect(() => {
        api.get(`/admin/finance/cogs-per-case?limit=30&include_all=${cogsIncludeAll}`).then((r) => setCogs(r.data)).catch(() => {});
    }, [cogsIncludeAll]);

    if (loading) return <div className="text-muted-foreground text-sm py-10 text-center">Loading reports…</div>;

    return (
        <div className="space-y-5" data-testid="finance-reports-tab">
            {aging && (
                <>
                    <ArAging kind="B2B (Invisique™ → Provider)" data={aging.b2b} testid="ar-b2b" />
                    <ArAging kind="Patient (Provider → Patient)" data={aging.patient} testid="ar-patient" />
                </>
            )}
            <RevenueTrend rows={trend} />
            {cogs && <UnitEconomics data={cogs} includeAll={cogsIncludeAll} onToggle={setCogsIncludeAll} />}
        </div>
    );
}


function ArAging({ kind, data, testid }) {
    const buckets = ["0_30", "31_60", "61_90", "90_plus"];
    const totalOutstanding = buckets.reduce((s, b) => s + (data[b]?.outstanding || 0), 0);
    const max = Math.max(1, ...buckets.map((b) => data[b]?.outstanding || 0));
    return (
        <div className="rounded-2xl border border-border bg-card p-5" data-testid={testid}>
            <div className="flex items-center justify-between gap-3 mb-4">
                <div>
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">AR Aging — {kind}</div>
                    <div className="font-display text-2xl font-bold tabular-nums mt-0.5">{formatINR(totalOutstanding)} outstanding</div>
                </div>
                {totalOutstanding > 0 && <Warning size={22} weight="fill" className="text-amber-600" />}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                {buckets.map((b) => {
                    const row = data[b] || { count: 0, outstanding: 0, label: b };
                    const pct = Math.round((row.outstanding / max) * 100);
                    const isLate = b === "61_90" || b === "90_plus";
                    return (
                        <div key={b} className={`rounded-xl border p-3 ${isLate && row.outstanding > 0 ? "border-rose-200 bg-rose-50" : "border-border bg-card"}`} data-testid={`${testid}-${b}`}>
                            <div className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground">{row.label}</div>
                            <div className={`font-display text-xl font-bold tabular-nums mt-1 ${isLate && row.outstanding > 0 ? "text-rose-800" : ""}`}>{formatINR(row.outstanding)}</div>
                            <div className="text-[11px] text-muted-foreground mt-0.5">{row.count} invoice{row.count === 1 ? "" : "s"}</div>
                            <div className="h-1.5 rounded-full bg-secondary mt-2 overflow-hidden">
                                <div className={`h-full ${isLate ? "bg-rose-500" : "bg-amber-500"}`} style={{ width: `${pct}%` }} />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}


function RevenueTrend({ rows }) {
    if (!rows?.length) {
        return (
            <div className="rounded-2xl border border-border bg-card p-5">
                <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground mb-2">Revenue trend (6 months)</div>
                <div className="text-sm text-muted-foreground py-6 text-center">No revenue history yet.</div>
            </div>
        );
    }
    const max = Math.max(1, ...rows.map((r) => Math.max(r.b2b_issued, r.patient_issued)));
    return (
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="revenue-trend">
            <div className="flex items-center gap-2 mb-3">
                <TrendUp size={16} weight="fill" className="text-emerald-600" />
                <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Revenue trend (6 months)</div>
            </div>
            <div className="grid grid-cols-6 gap-2">
                {rows.slice(-6).map((r) => (
                    <div key={r.month} className="text-center" data-testid={`revenue-bar-${r.month}`}>
                        <div className="h-24 flex items-end justify-center gap-1 mb-1">
                            <div title={`B2B issued: ${formatINR(r.b2b_issued)}`} className="w-3 bg-primary rounded-t" style={{ height: `${(r.b2b_issued / max) * 100}%`, minHeight: r.b2b_issued > 0 ? "2px" : 0 }} />
                            <div title={`Patient issued: ${formatINR(r.patient_issued)}`} className="w-3 bg-amber-500 rounded-t" style={{ height: `${(r.patient_issued / max) * 100}%`, minHeight: r.patient_issued > 0 ? "2px" : 0 }} />
                        </div>
                        <div className="text-[9px] font-semibold text-muted-foreground uppercase">{r.month.slice(-2)}/{r.month.slice(2, 4)}</div>
                    </div>
                ))}
            </div>
            <div className="flex items-center gap-4 mt-3 text-[10px] uppercase tracking-wide text-muted-foreground">
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded bg-primary" /> B2B issued</span>
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded bg-amber-500" /> Patient issued</span>
            </div>
        </div>
    );
}


function UnitEconomics({ data, includeAll, onToggle }) {
    /* Unit Economics — per-case COGS view aggregated from stock_movements.
       Shows the team's most expensive cases at a glance + roll-up KPIs.
       Empty state when no cases have logged materials yet. */
    const rows = data?.rows || [];
    const summary = data?.summary || { case_count: 0, total_cogs: 0, avg_cogs_per_case: 0, zero_cogs_count: 0 };
    return (
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="unit-economics">
            <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-2">
                    <Package size={16} weight="fill" className="text-primary" />
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Unit economics — COGS per case</div>
                </div>
                <label className="inline-flex items-center gap-2 cursor-pointer select-none text-xs" data-testid="cogs-include-all-toggle">
                    <input
                        type="checkbox"
                        checked={!!includeAll}
                        onChange={(e) => onToggle?.(e.target.checked)}
                        className="rounded border-border"
                        data-testid="cogs-include-all-checkbox"
                    />
                    <span className="text-muted-foreground">
                        Show cases without logged materials
                        {summary.zero_cogs_count > 0 && includeAll && (
                            <span className="ml-1.5 text-rose-700 font-semibold">({summary.zero_cogs_count} unlogged)</span>
                        )}
                    </span>
                </label>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-4">
                <SmallKpi label="Cases with materials" value={summary.case_count} tone="primary" />
                <SmallKpi label="Total COGS (est)" value={formatINR(summary.total_cogs)} tone="rose" />
                <SmallKpi label="Avg per case" value={formatINR(summary.avg_cogs_per_case)} tone="amber" />
            </div>
            {rows.length === 0 ? (
                <div className="text-sm text-muted-foreground py-6 text-center" data-testid="unit-economics-empty">
                    No case-level material consumption yet. Planners can log usage from the case "My notes" tab.
                </div>
            ) : (
                <div className="rounded-xl border border-border overflow-hidden">
                    <div className="grid grid-cols-12 gap-3 px-4 py-2 text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground border-b border-border bg-secondary/30">
                        <div className="col-span-2">Case</div>
                        <div className="col-span-3">Patient / Doctor</div>
                        <div className="col-span-2">Status</div>
                        <div className="col-span-3">Top materials</div>
                        <div className="col-span-2 text-right">Total cost</div>
                    </div>
                    {rows.map((r) => {
                        const isUnlogged = r.total_cost === 0 && (r.lines || []).length === 0;
                        return (
                            <a
                                key={r.case_id}
                                href={`/admin/cases/${r.case_id}`}
                                className={`grid grid-cols-12 gap-3 px-4 py-3 border-b border-border last:border-0 items-center hover:bg-secondary/30 transition text-sm ${isUnlogged ? "bg-rose-50/40" : ""}`}
                                data-testid={`unit-economics-row-${r.case_id}`}
                            >
                                <div className="col-span-2 font-mono text-xs font-bold">{r.case_code || r.case_id.slice(0, 8)}</div>
                                <div className="col-span-3 min-w-0">
                                    <div className="font-semibold truncate text-sm">{r.patient_name || "—"}</div>
                                    <div className="text-[11px] text-muted-foreground truncate">{r.doctor_name || "—"}</div>
                                </div>
                                <div className="col-span-2 text-xs text-muted-foreground capitalize truncate">{(r.status || "—").replace(/_/g, " ")}</div>
                                <div className="col-span-3 flex items-center gap-1 flex-wrap">
                                    {isUnlogged ? (
                                        <span className="text-[10px] uppercase tracking-wide font-bold text-rose-700">No materials logged</span>
                                    ) : (
                                        <>
                                            {r.lines.slice(0, 2).map((l) => (
                                                <span key={l.material_id} className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground font-medium truncate" title={l.material_name}>
                                                    {l.material_sku} ×{l.quantity}
                                                </span>
                                            ))}
                                            {r.lines.length > 2 && <span className="text-[10px] text-muted-foreground">+{r.lines.length - 2}</span>}
                                        </>
                                    )}
                                </div>
                                <div className="col-span-2 text-right">
                                    <div className={`font-bold tabular-nums ${isUnlogged ? "text-rose-700" : ""}`}>{formatINR(r.total_cost)}</div>
                                    <ArrowRight size={11} className="inline text-muted-foreground" />
                                </div>
                            </a>
                        );
                    })}
                </div>
            )}
        </div>
    );
}


function SmallKpi({ label, value, tone }) {
    const cls = tone === "rose"
        ? "border-rose-200 bg-rose-50 text-rose-900"
        : tone === "amber"
            ? "border-amber-200 bg-amber-50 text-amber-900"
            : "border-primary/30 bg-primary/5 text-primary";
    return (
        <div className={`rounded-xl border p-3 ${cls}`}>
            <div className="font-display text-xl font-bold tabular-nums">{value}</div>
            <div className="text-[10px] font-bold tracking-[0.14em] uppercase text-muted-foreground mt-0.5">{label}</div>
        </div>
    );
}
